import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, filter, interval, map, Observable, startWith } from 'rxjs';
import jwtDecode from 'jwt-decode';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private storageService = inject(StorageService);

  private authTokenUpdate = new BehaviorSubject<boolean>(false);

  tokenChanges$ = this.authTokenUpdate.asObservable();
  tokenExpired$: Observable<boolean> = interval(1000).pipe(
    startWith(0),
    map(() => this.isTokenAlreadyExpired()),
    distinctUntilChanged(),
    filter(Boolean),
  );

  get token(): string | null {
    return this.storageService.authToken;
  }

  isTokenValid(token: string | null): boolean {
    if (!token) {
      return false;
    }
    try {
      const decodedToken = jwtDecode<Record<string, unknown>>(token);
      const currentTime = Math.floor(Date.now() / 1000);
      const exp = decodedToken['exp'];
      return typeof exp === 'number' && exp > currentTime;
    } catch (error) {
      return false;
    }
  }

  /**
   * Shopping journey id of the current session, or null when there is no valid
   * token. Used as the business correlation id in telemetry.
   */
  get journeyId(): string | null {
    return this.token ? this.getJourneyIdFromToken(this.token) : null;
  }

  getJourneyIdFromToken(token: string): string | null {
    return this.getClaim(token, 'ShoppingJourneyId');
  }

  getLatestToken(token1: string | null, token2: string | null): string | null {
    if (!token1 && !token2) {
      return null;
    }
    if (!token1) {
      return token2;
    }
    if (!token2) {
      return token1;
    }
    try {
      const decoded1 = jwtDecode<Record<string, unknown>>(token1);
      const decoded2 = jwtDecode<Record<string, unknown>>(token2);
      const exp1 = decoded1['exp'];
      const exp2 = decoded2['exp'];
      if (typeof exp1 === 'number' && typeof exp2 === 'number') {
        return exp1 > exp2 ? token1 : token2;
      }
      return token1;
    } catch (error) {
      return token1 || token2;
    }
  }

  setAuthToken(token: string | null): void {
    this.storageService.authToken = token;
    this.authTokenUpdate.next(true);
  }

  private getClaim(token: string, claim: string): string | null {
    if (!token) {
      return null;
    }
    try {
      const decodedToken = jwtDecode<Record<string, unknown>>(token);
      const value = decodedToken[claim];
      return typeof value === 'string' ? value : null;
    } catch (error) {
      return null;
    }
  }

  private isTokenAlreadyExpired(): boolean {
    const token = this.token;
    if (!token) return true;
    try {
      const decoded = jwtDecode<Record<string, unknown>>(token);
      const currentTime = Math.floor(Date.now() / 1000);
      const exp = decoded['exp'];
      return typeof exp !== 'number' || exp <= currentTime;
    } catch {
      return true;
    }
  }

  logout(): void {
    this.storageService.authToken = null;
    this.authTokenUpdate.next(false);
  }
}
